import { formatEmail } from "./formats/formatEmail";
import { formatFullName } from "./formats/formatFullName";

export const onlyDigits = (value: string): string => value.replace(/\D+/g, "");

// Retorna o(s) gênero(s) oposto(s) para busca
export function getOppositeGender(gender: string): string[] {
  if (gender === "Masculino") return ["Feminino"];
  if (gender === "Feminino") return ["Masculino"];
  return [];
}

export const isValidEmail = (value: string | null | undefined): boolean => {
  if (!value) return false;

  const email = formatEmail(value);

  /**
   * ^[^\s@]+          -> Início: Qualquer caractere que não seja espaço ou @
   * @                 -> Precisa ter um @
   * [^\s@.]+          -> Domínio: Não pode começar com ponto ou ter @
   * (?:                -> Grupo de repetição para subdomínios
   * \.[^\s@.]+       -> Um ponto seguido de caracteres válidos
   * )+                 -> Precisa repetir pelo menos uma vez (ex: .com)
   * $                 -> Fim
   */
  const emailRegex = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

  // Validações extras que o Regex às vezes deixa passar:
  const isTooLong = email.length > 254;
  const hasDoubleDots = email.includes("..");
  const startsOrEndsWithDot = email.startsWith(".") || email.endsWith(".");

  const isValid =
    emailRegex.test(email) &&
    !isTooLong &&
    !hasDoubleDots &&
    !startsOrEndsWithDot;

  return isValid;
};

export const hasFirstAndLastName = (value: string): boolean => {
  const name = formatFullName(value);
  if (name.length === 0) return false;

  const parts = name.split(" ").filter(Boolean);
  if (parts.length < 2) return false;

  const partRegex = /^[^\d\W_]+(?:[-'][^\d\W_]+)*$/u;
  return parts.every((part) => partRegex.test(part));
};

export const hasUppercaseLetter = (value: string): boolean =>
  /[A-Z]/.test(value);

export const hasLowercaseLetter = (value: string): boolean =>
  /[a-z]/.test(value);

export const hasNumber = (value: string): boolean => /\d/.test(value);

export const hasSpecialCharacter = (value: string): boolean =>
  /[^A-Za-z0-9]/.test(value);

export const isValidPassword = (value: string): boolean =>
  hasUppercaseLetter(value) &&
  hasLowercaseLetter(value) &&
  hasNumber(value) &&
  hasSpecialCharacter(value);

export const isValidPhoneBR = (value: string): boolean => {
  const digits = onlyDigits(value);
  return digits.length === 10 || digits.length === 11;
};

// Calcula idade a partir apenas do ano de nascimento assumindo aniversário em 31/12.
export const getAge = (
  birthYear: string | number | undefined,
): number | null => {
  if (!birthYear) return null;

  const year =
    typeof birthYear === "string" ? parseInt(birthYear, 10) : birthYear;

  if (!Number.isFinite(year) || year < 1900) return null;

  const currentYear = new Date().getFullYear();
  if (year > currentYear) return null;

  // Sua lógica original: compara com o último dia do ano
  const birthdayThisYear = new Date(currentYear, 11, 31);
  const today = new Date();

  let age = currentYear - year;

  // Se hoje ainda não passamos do "prazo" do seu critério, subtrai 1
  if (today < birthdayThisYear) {
    // Como 31/12 é o último dia, essa condição quase sempre será verdadeira
    // até o último dia do ano, mantendo a idade "congelada" conforme sua regra.
    age -= 1;
  }

  return age >= 0 ? age : null;
};

export const noPhoto = (gender?: string): string => {
  if (gender === "Masculino") {
    return "/site/male.svg";
  }

  if (gender === "Feminino") {
    return "/site/female.svg";
  }

  return "/site/avatar-default.png";
};

export const slugify = (text: string) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .normalize("NFD") // Remove acentos
    .replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/\s+/g, "-") // Substitui espaços por -
    .replace(/[^\w-]+/g, "") // Remove caracteres especiais
    .replace(/--+/g, "-"); // Remove hífens duplos
};
